import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApiClient } from "../../../data/clients/ApiClient";

type Lang = "da" | "en";

enum Step {
  VALIDATION = 1,
  EMAIL_ENTRY = 2,
  SUCCESS = 3,
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const JETSPORT_LOGO =
  "https://stkitmakerdev.blob.core.windows.net/b2c-login-page/logo-jetsport.png";

interface Copy {
  signIn: string;
  steps: string[];
  s1_h1: string;
  s1_sub: string;
  s1_info: string;
  s1_userLabel: string;
  s1_userPh: string;
  s1_passLabel: string;
  s1_passPh: string;
  s1_helperTitle: string;
  s1_helperBody: string;
  s1_helperStar: string;
  s1_seeOld: string;
  old_title: string;
  old_sub: string;
  old_close: string;
  s1_validate: string;
  s1_validating: string;
  s1_forgot: string;
  s1_errInvalid: string;
  s1_errGeneric: string;
  s2_h1: string;
  s2_sub: string;
  s2_first: string;
  s2_firstPh: string;
  s2_last: string;
  s2_lastPh: string;
  s2_email: string;
  s2_emailPh: string;
  s2_phone: string;
  s2_phonePh: string;
  s2_note: string;
  s2_back: string;
  s2_create: string;
  s2_creating: string;
  s2_errGeneric: string;
  s3_h1: string;
  s3_sub: string;
  s3_emailFallback: string;
  s3_next: string[];
  s3_goLogin: string;
  t_tagline: string;
  t_moved: string;
  t_designCard: string;
  t_headline: string;
  t_subtext: string;
  feats: { title: string; desc: string }[];
  support: string;
}

const STRINGS: Record<Lang, Copy> = {
  da: {
    signIn: "Allerede flyttet? Log ind",
    steps: ["Verificér", "Opret konto", "Færdig"],
    s1_h1: "Flyt din konto til KitMaker",
    s1_sub: "Log ind med dit login fra den gamle webshop, så flytter vi din konto med det samme.",
    s1_info: "Sådan virker det: bekræft din gamle konto, opret din nye, og vi sender en adgangskode til din e-mail.",
    s1_userLabel: "Brugernavn fra gammel webshop",
    s1_userPh: "Anders Andersen",
    s1_passLabel: "Adgangskode",
    s1_passPh: "Din nuværende kode",
    s1_helperTitle: "Det er det samme login som den gamle webshop",
    s1_helperBody: "Brug det brugernavn og den adgangskode, du plejer at bruge på den gamle Jet Sport-webshop.",
    s1_helperStar: "↑ Dit brugernavn",
    s1_seeOld: "Se den gamle login →",
    old_title: "Sådan så din gamle login ud",
    old_sub: "Det er præcis samme brugernavn og adgangskode, du brugte på den gamle Jet Sport-webshop.",
    old_close: "Luk",
    s1_validate: "Validér konto",
    s1_validating: "Validerer …",
    s1_forgot: "Kan du ikke huske dit login?",
    s1_errInvalid: "Forkert brugernavn eller adgangskode. Tjek dine oplysninger og prøv igen.",
    s1_errGeneric: "Der opstod en fejl under valideringen. Prøv igen.",
    s2_h1: "Opret din nye konto",
    s2_sub: "Udfyld dine oplysninger, så opretter vi din KitMaker-konto.",
    s2_first: "Fornavn",
    s2_firstPh: "Anders",
    s2_last: "Efternavn",
    s2_lastPh: "Andersen",
    s2_email: "E-mail",
    s2_emailPh: "dig@firma.dk",
    s2_phone: "Telefon",
    s2_phonePh: "12 34 56 78",
    s2_note: "Vi sender en adgangskode til din e-mail. Fremover logger du ind med din e-mail, ikke dit gamle brugernavn.",
    s2_back: "Tilbage",
    s2_create: "Opret konto",
    s2_creating: "Opretter …",
    s2_errGeneric: "Der opstod en fejl. Prøv igen.",
    s3_h1: "Din konto er oprettet!",
    s3_sub: "Vi har sendt en adgangskode til din e-mail. Sådan kommer du ind første gang:",
    s3_emailFallback: "din e-mail",
    s3_next: [
      "Tjek din e-mail for adgangskoden, vi lige har sendt.",
      "Log ind på KitMaker med din e-mail og den tilsendte kode.",
      "Første gang beder vi dig vælge din egen nye adgangskode.",
    ],
    s3_goLogin: "Gå til KitMaker login",
    t_tagline: "Sportstryk & broderi — gjort enkelt",
    t_moved: "Vi er flyttet",
    t_designCard: "Dit design",
    t_headline: "Din nye platform til logoer og tryk",
    t_subtext: "Se live-preview af dit tryk, design selv, og find og bestil dine logoer lynhurtigt.",
    feats: [
      { title: "Live preview", desc: "Se dit tryk med det samme." },
      { title: "Design selv", desc: "Byg logoer og navnetryk online." },
      { title: "Find & bestil hurtigt", desc: "Søg og bestil på sekunder." },
    ],
    support: "Brug for hjælp? Skriv til post@jetsport.dk",
  },
  en: {
    signIn: "Already moved? Sign in",
    steps: ["Verify", "Create account", "Done"],
    s1_h1: "Bring your account to KitMaker",
    s1_sub: "Sign in with your old webshop login and we'll move your account right away.",
    s1_info: "How it works: confirm your old account, create your new one, and we'll email you a password.",
    s1_userLabel: "Username from old webshop",
    s1_userPh: "Anders Andersen",
    s1_passLabel: "Password",
    s1_passPh: "Your current password",
    s1_helperTitle: "It's the same login as the old webshop",
    s1_helperBody: "Use the username and password you normally use on the old Jet Sport webshop.",
    s1_helperStar: "↑ Your username",
    s1_seeOld: "See the old login →",
    old_title: "This is what your old login looked like",
    old_sub: "It's the exact same username and password you used on the old Jet Sport webshop.",
    old_close: "Close",
    s1_validate: "Validate account",
    s1_validating: "Validating …",
    s1_forgot: "Can't remember your login?",
    s1_errInvalid: "Invalid username or password. Please check your details and try again.",
    s1_errGeneric: "Something went wrong while validating. Please try again.",
    s2_h1: "Create your new account",
    s2_sub: "Fill in your details and we'll create your KitMaker account.",
    s2_first: "First name",
    s2_firstPh: "Anders",
    s2_last: "Last name",
    s2_lastPh: "Andersen",
    s2_email: "Email",
    s2_emailPh: "you@company.com",
    s2_phone: "Phone",
    s2_phonePh: "+45 12 34 56 78",
    s2_note: "We'll email you a password. From now on you sign in with your email, not your old username.",
    s2_back: "Back",
    s2_create: "Create account",
    s2_creating: "Creating …",
    s2_errGeneric: "Something went wrong. Please try again.",
    s3_h1: "Your account is created!",
    s3_sub: "We've sent a password to your email. Here's how to get in the first time:",
    s3_emailFallback: "your email",
    s3_next: [
      "Check your email for the password we just sent.",
      "Sign in to KitMaker with your email and that password.",
      "On first login we'll ask you to choose your own new password.",
    ],
    s3_goLogin: "Go to KitMaker login",
    t_tagline: "Sports print & embroidery — made simple",
    t_moved: "We've moved",
    t_designCard: "Your design",
    t_headline: "Your new home for logos and print",
    t_subtext: "See a live preview of your print, design your own, and find and order your logos in seconds.",
    feats: [
      { title: "Live preview", desc: "See your print instantly." },
      { title: "Design your own", desc: "Build logos and name prints online." },
      { title: "Fast search & order", desc: "Search and order in seconds." },
    ],
    support: "Need help? Email post@jetsport.dk",
  },
};

/* ---------------- icons (inline stroke SVG) ---------------- */
const stroke = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

const UserIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" {...stroke}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20c0-3.3 3.6-6 8-6s8 2.7 8 6" />
  </svg>
);
const LockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" {...stroke}>
    <rect x="4" y="10" width="16" height="10" rx="2" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
  </svg>
);
const MailIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" {...stroke}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 6 9-6" />
  </svg>
);
const PhoneIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" {...stroke}>
    <path d="M4 5c0 8.3 6.7 15 15 15v-3.5l-4-1.5-2 2a12 12 0 0 1-6-6l2-2L7.5 5H4Z" />
  </svg>
);
const InfoIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" {...stroke}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5M12 8h.01" />
  </svg>
);

/* ---------------- t-shirt design mockup ---------------- */
const DesignMockup = () => (
  <svg viewBox="0 0 200 200" width="150" height="150" aria-hidden="true">
    <path
      d="M64 40 84 32a16 16 0 0 0 32 0l20 8 24 16-14 22-14-8v76a6 6 0 0 1-6 6H74a6 6 0 0 1-6-6V78l-14 8-14-22 24-16Z"
      fill="#fff"
      stroke="#c3ccdd"
      strokeWidth="2.5"
      strokeDasharray="6 5"
      strokeLinejoin="round"
    />
    {/* chest chevrons */}
    <g>
      <path d="M74 66h16l-5 9H69Z" fill="#ffb168" />
      <path d="M82 66h16l-5 9H77Z" fill="#ff9236" />
      <path d="M90 66h16l-5 9H85Z" fill="#f4741a" />
    </g>
    {/* center print dot grid */}
    <g fill="#18b89c">
      {[0, 1, 2, 3].map((r) =>
        [0, 1, 2, 3].map((c) => (
          <circle key={`${r}-${c}`} cx={84 + c * 11} cy={104 + r * 11} r="3.6" />
        ))
      )}
    </g>
  </svg>
);

/* ---------------- reusable field ---------------- */
interface FieldProps {
  label: string;
  icon?: React.ReactNode;
  type?: string;
  name?: string;
  value: string;
  placeholder?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  autoComplete?: string;
  extra?: React.ReactNode;
}

const Field = ({ label, icon, type = "text", name, value, placeholder, onChange, disabled, autoComplete, extra }: FieldProps) => (
  <div className="mb-5">
    <div className="mb-2 flex items-center justify-between">
      <label className="text-[12px] font-bold uppercase tracking-wide text-[#8a94a6]">{label}</label>
      {extra}
    </div>
    <div className="relative">
      {icon && (
        <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#9aa4b5]">{icon}</span>
      )}
      <input
        type={type}
        name={name}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        disabled={disabled}
        autoComplete={autoComplete}
        className={`w-full rounded-[12px] border border-[#e3e8f1] bg-white py-[15px] text-[15px] font-medium text-[#2b3242] outline-none transition placeholder:font-normal placeholder:text-[#9aa4b5] focus:border-[#343b4a] focus:ring-4 focus:ring-[#343b4a]/[0.06] ${icon ? "pl-[48px] pr-4" : "px-[18px]"}`}
      />
    </div>
  </div>
);

const Banner = ({ children }: { children: React.ReactNode }) => (
  <div className="mb-6 flex gap-3 rounded-[12px] border border-[#eaeef6] bg-[#f7f9fd] p-4 text-[13px] font-medium leading-relaxed text-[#5b6577]">
    <span className="mt-0.5 shrink-0 text-[#9aa4b5]">
      <InfoIcon />
    </span>
    <span>{children}</span>
  </div>
);

const ErrorBox = ({ errors }: { errors: string[] }) =>
  errors.length === 0 ? null : (
    <div className="mb-4 rounded-[10px] border border-[#f4c7c7] bg-[#fdeeee] p-3.5">
      {errors.map((e, i) => (
        <p key={i} className="text-[13px] font-semibold text-[#c0392b]">
          {e}
        </p>
      ))}
    </div>
  );

/* ================================================================= */

export function MigrationPage() {
  const navigate = useNavigate();
  const { validateOldCredentials, migrateFromOldWebshop } = useApiClient();

  const [lang, setLang] = useState<Lang>("da");
  const [step, setStep] = useState<Step>(Step.VALIDATION);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [hintOpen, setHintOpen] = useState(false);
  const [showOldLogin, setShowOldLogin] = useState(false);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const [validation, setValidation] = useState<{ username: string; password: string; customerNo: string } | null>(null);

  const t = STRINGS[lang];
  const emailValid = EMAIL_RE.test(email);
  const canCreate = firstName.trim().length > 0 && lastName.trim().length > 0 && emailValid;

  const handleValidate = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);
    setIsLoading(true);
    try {
      const res = await validateOldCredentials(username, password);
      if (res?.isValid) {
        setValidation({
          username: res.username || username,
          password: res.password || password,
          customerNo: res.customerNo || "",
        });
        setStep(Step.EMAIL_ENTRY);
      } else {
        setErrors([res?.errorMessage || t.s1_errInvalid]);
      }
    } catch {
      setErrors([t.s1_errGeneric]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validation || !canCreate) return;
    setErrors([]);
    setIsLoading(true);
    try {
      const res = await migrateFromOldWebshop(
        validation.username,
        validation.password,
        firstName,
        lastName,
        email,
        phone,
        validation.customerNo
      );
      if (res?.success) {
        setStep(Step.SUCCESS);
        setTimeout(() => navigate("/"), 10000);
      } else {
        setErrors([res?.message || t.s2_errGeneric]);
      }
    } catch {
      setErrors([t.s2_errGeneric]);
    } finally {
      setIsLoading(false);
    }
  };

  const stepStyles = (index: number) => {
    const n = step; // 1..3
    if (index < n) return { circle: "bg-[#3cb44a] text-white", label: "text-[#3cb44a]" };
    if (index === n) return { circle: "bg-[#343b4a] text-white", label: "text-[#2b3242]" };
    return { circle: "bg-[#eef1f7] text-[#9aa4b5]", label: "text-[#9aa4b5]" };
  };

  return (
    <div className="fixed inset-0 z-[60] flex overflow-hidden bg-white" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
        {/* ---------- teaser panel ---------- */}
        <aside className="relative hidden w-[42%] max-w-[620px] shrink-0 flex-col overflow-hidden bg-[#dae4f6] px-12 py-12 lg:flex">
          <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#e8eefb]" />
          <div className="pointer-events-none absolute -left-20 top-1/3 h-52 w-52 rounded-full bg-[#cbd8f1] opacity-70" />
          <div className="pointer-events-none absolute -bottom-16 right-8 h-44 w-44 rounded-full bg-[#e2eafb]" />

          <div className="relative">
            <div className="text-[23px] font-black tracking-tight text-[#2b3242]">KitMaker</div>
            <div className="mt-1 text-[12.5px] font-bold text-[#5b6b86]">{t.t_tagline}</div>
            <span className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-[#cddbf3] bg-[#eef4ff] px-3 py-1 text-[12px] font-bold text-[#3b5a9a]">
              <span aria-hidden="true">👋</span> {t.t_moved}
            </span>
          </div>

          <div className="relative my-8 flex flex-1 flex-col items-center justify-center">
            <div className="w-[200px] -rotate-3 rounded-[18px] bg-white p-5 shadow-[0_22px_46px_-20px_rgba(40,60,110,0.42)]">
              <div className="mb-1 text-center text-[12px] font-extrabold uppercase tracking-wide text-[#8a94a6]">
                {t.t_designCard}
              </div>
              <div className="flex justify-center">
                <DesignMockup />
              </div>
            </div>
            <h2 className="mt-7 max-w-[340px] text-center text-[25px] font-extrabold leading-tight tracking-tight text-[#2b3242]">
              {t.t_headline}
            </h2>
            <p className="mt-2.5 max-w-[340px] text-center text-[14px] font-semibold leading-relaxed text-[#5b6b86]">
              {t.t_subtext}
            </p>
          </div>

          <div className="relative flex flex-col gap-3.5">
            {t.feats.map((f, i) => {
              const tile = ["bg-[#e6f7f3] text-[#17b89d]", "bg-[#fdeee2] text-[#f4741a]", "bg-[#eef1f7] text-[#343b4a]"][i];
              return (
                <div key={f.title} className="flex items-center gap-3">
                  <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] ${tile}`}>
                    <svg width="18" height="18" viewBox="0 0 24 24" {...stroke}>
                      {i === 0 && <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="2.5" /></>}
                      {i === 1 && <path d="m15 5 4 4L8 20l-5 1 1-5Z" />}
                      {i === 2 && <><circle cx="11" cy="11" r="6" /><path d="m20 20-3.5-3.5" /></>}
                    </svg>
                  </span>
                  <div>
                    <div className="text-[14px] font-extrabold text-[#2b3242]">{f.title}</div>
                    <div className="text-[12.5px] font-semibold text-[#5b6b86]">{f.desc}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </aside>

        {/* ---------- form column ---------- */}
        <main className="min-w-0 flex-1 overflow-y-auto">
          <div className="mx-auto flex min-h-full w-full max-w-[520px] flex-col px-6 py-8 sm:px-12">
          {/* header row */}
          <div className="mb-8 flex items-center justify-between gap-3">
            <div className="text-[17px] font-black text-[#2b3242] lg:hidden">KitMaker</div>
            <div className="hidden lg:block" />
            <div className="flex items-center gap-4">
              <div className="flex rounded-full bg-[#f2f5fb] p-0.5 text-[12px] font-extrabold">
                {(["da", "en"] as Lang[]).map((l) => (
                  <button
                    key={l}
                    type="button"
                    onClick={() => setLang(l)}
                    className={`rounded-full px-3 py-1 uppercase transition ${lang === l ? "bg-[#343b4a] text-white" : "text-[#5b6577]"}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => navigate("/")}
                className="text-[13px] font-bold text-[#5b6577] underline-offset-2 hover:text-[#343b4a] hover:underline"
              >
                {t.signIn}
              </button>
            </div>
          </div>

          <div className="flex flex-1 flex-col justify-center py-2">
          {/* step indicator */}
          <div className="mb-9 flex w-full items-center">
            {t.steps.map((label, i) => {
              const index = i + 1;
              const st = stepStyles(index);
              return (
                <React.Fragment key={label}>
                  <div className="flex flex-col items-center">
                    <span className={`flex h-7 w-7 items-center justify-center rounded-full text-[12px] font-extrabold transition ${st.circle}`}>
                      {index < step ? "✓" : index}
                    </span>
                    <span className={`mt-1.5 whitespace-nowrap text-[11px] font-extrabold ${st.label}`}>{label}</span>
                  </div>
                  {index < 3 && (
                    <span className={`mx-2 mb-4 h-[2px] flex-1 rounded ${index < step ? "bg-[#3cb44a]" : "bg-[#eef1f7]"}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* step content */}
          <div key={step} className="w-full animate-[fadeUp_0.32s_ease]">
            {step === Step.VALIDATION && (
              <form onSubmit={handleValidate}>
                <h1 className="text-[33px] font-extrabold leading-[1.12] tracking-tight text-[#1b2230]">{t.s1_h1}</h1>
                <p className="mt-3 mb-7 text-[15.5px] leading-relaxed text-[#7a8699]">{t.s1_sub}</p>
                <Banner>{t.s1_info}</Banner>

                <Field
                  label={t.s1_userLabel}
                  icon={<UserIcon />}
                  value={username}
                  placeholder={t.s1_userPh}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={isLoading}
                  autoComplete="username"
                  extra={
                    <button
                      type="button"
                      onClick={() => setHintOpen((v) => !v)}
                      aria-label="Hjælp"
                      className="text-[#f4741a] transition hover:scale-110"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="m12 2 2.9 6.1 6.7.8-5 4.6 1.3 6.6L12 17l-5.9 3.1 1.3-6.6-5-4.6 6.7-.8Z" />
                      </svg>
                    </button>
                  }
                />

                {hintOpen && (
                  <div className="-mt-1 mb-4 rounded-[12px] border border-[#e6ebf4] bg-[#f6f8fc] p-4">
                    <div className="text-[13.5px] font-extrabold text-[#2b3242]">{t.s1_helperTitle}</div>
                    <p className="mt-1 text-[12.5px] font-semibold leading-relaxed text-[#5b6577]">{t.s1_helperBody}</p>
                    <div className="mt-3 rounded-[10px] border border-[#e6ebf4] bg-[#f1f1f1] p-3" style={{ fontFamily: "Arial, Helvetica, sans-serif" }}>
                      <div className="text-[11px] font-bold text-[#555]">Kundelogin</div>
                      <div className="mt-1.5 flex items-center gap-2">
                        <div className="flex-1 border border-[#d5121b] bg-white px-2.5 py-1.5 text-[12px] font-semibold text-[#333]">
                          Anders Andersen
                        </div>
                        <span className="text-[#f4741a]" aria-hidden="true">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="m12 2 2.9 6.1 6.7.8-5 4.6 1.3 6.6L12 17l-5.9 3.1 1.3-6.6-5-4.6 6.7-.8Z" />
                          </svg>
                        </span>
                      </div>
                      <div className="mt-1 text-right text-[11px] font-bold text-[#f4741a]">{t.s1_helperStar}</div>
                      <div className="mt-2 flex h-6 items-center justify-center bg-[#d5121b] text-[10px] font-bold text-white">Login</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowOldLogin(true)}
                      className="mt-2.5 inline-block text-[12.5px] font-bold text-[#3b5a9a] hover:underline"
                    >
                      {t.s1_seeOld}
                    </button>
                  </div>
                )}

                <Field
                  label={t.s1_passLabel}
                  icon={<LockIcon />}
                  type="password"
                  value={password}
                  placeholder={t.s1_passPh}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  autoComplete="current-password"
                />

                <ErrorBox errors={errors} />

                <button
                  type="submit"
                  disabled={isLoading || !username || !password}
                  className="mt-1 w-full rounded-[10px] bg-[#343b4a] py-4 text-[15px] font-extrabold text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:bg-[#c4cbd8]"
                >
                  {isLoading ? t.s1_validating : t.s1_validate}
                </button>
                <div className="mt-3 text-center text-[13px] font-semibold text-[#9aa4b5]">{t.s1_forgot}</div>
              </form>
            )}

            {step === Step.EMAIL_ENTRY && (
              <form onSubmit={handleCreate}>
                <h1 className="text-[33px] font-extrabold leading-[1.12] tracking-tight text-[#1b2230]">{t.s2_h1}</h1>
                <p className="mt-3 mb-7 text-[15.5px] leading-relaxed text-[#7a8699]">{t.s2_sub}</p>

                <div className="flex gap-3">
                  <div className="flex-1">
                    <Field label={t.s2_first} value={firstName} placeholder={t.s2_firstPh} onChange={(e) => setFirstName(e.target.value)} disabled={isLoading} autoComplete="given-name" />
                  </div>
                  <div className="flex-1">
                    <Field label={t.s2_last} value={lastName} placeholder={t.s2_lastPh} onChange={(e) => setLastName(e.target.value)} disabled={isLoading} autoComplete="family-name" />
                  </div>
                </div>
                <Field label={t.s2_email} icon={<MailIcon />} type="email" value={email} placeholder={t.s2_emailPh} onChange={(e) => setEmail(e.target.value)} disabled={isLoading} autoComplete="email" />
                <Field label={t.s2_phone} icon={<PhoneIcon />} type="tel" value={phone} placeholder={t.s2_phonePh} onChange={(e) => setPhone(e.target.value)} disabled={isLoading} autoComplete="tel" />

                <Banner>{t.s2_note}</Banner>
                <ErrorBox errors={errors} />

                <div className="mt-1 flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setErrors([]);
                      setStep(Step.VALIDATION);
                    }}
                    disabled={isLoading}
                    className="rounded-[10px] border-[1.5px] border-[#e2e8f2] bg-white px-6 py-4 text-[15px] font-extrabold text-[#343b4a] transition hover:bg-[#f6f8fc] disabled:opacity-60"
                  >
                    {t.s2_back}
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading || !canCreate}
                    className="flex-1 rounded-[10px] bg-[#343b4a] py-4 text-[15px] font-extrabold text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:bg-[#c4cbd8]"
                  >
                    {isLoading ? t.s2_creating : t.s2_create}
                  </button>
                </div>
              </form>
            )}

            {step === Step.SUCCESS && (
              <div className="text-center">
                <div className="mx-auto flex h-[76px] w-[76px] items-center justify-center rounded-full bg-[#e8f7ec]">
                  <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#3cb44a" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h1 className="mt-5 text-[28px] font-extrabold tracking-tight text-[#2b3242]">{t.s3_h1}</h1>
                <p className="mx-auto mt-2.5 max-w-[380px] text-[15px] leading-relaxed text-[#7a8699]">{t.s3_sub}</p>

                <div className="mx-auto mt-5 inline-flex items-center gap-2 rounded-full border border-[#e6ebf4] bg-[#f2f5fb] px-4 py-2 text-[14px] font-semibold text-[#2b3242]">
                  <span className="text-[#9aa4b5]">
                    <MailIcon />
                  </span>
                  {email || t.s3_emailFallback}
                </div>

                <div className="mx-auto mt-6 max-w-[420px] rounded-[14px] border border-[#e6ebf4] bg-[#f6f8fc] p-1.5 text-left">
                  {t.s3_next.map((line, i) => (
                    <div key={i} className={`flex items-center gap-3 px-3 py-3 ${i < 2 ? "border-b border-[#eaeef5]" : ""}`}>
                      <span className={`flex h-[25px] w-[25px] shrink-0 items-center justify-center rounded-full text-[12px] font-extrabold text-white ${i === 2 ? "bg-[#17b89d]" : "bg-[#343b4a]"}`}>
                        {i + 1}
                      </span>
                      <span className="text-[13.5px] font-semibold leading-snug text-[#43506b]">{line}</span>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => navigate("/")}
                  className="mt-6 w-full max-w-[420px] rounded-[10px] bg-[#343b4a] py-4 text-[15px] font-extrabold text-white transition hover:brightness-110"
                >
                  {t.s3_goLogin}
                </button>
              </div>
            )}
          </div>

          </div>

          {/* footer */}
          <div className="mt-8 w-full text-center text-[12.5px] font-bold text-[#9aa4b5]">
            {t.support}
          </div>
          </div>
        </main>

      {showOldLogin && (
        <div
          onClick={() => setShowOldLogin(false)}
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-[380px] overflow-hidden rounded-[18px] bg-white shadow-[0_34px_80px_-22px_rgba(0,0,0,0.55)]"
          >
            <div className="px-6 pt-6 text-center">
              <div className="text-[18px] font-extrabold tracking-tight text-[#2b3242]">{t.old_title}</div>
              <p className="mx-auto mt-1.5 max-w-[300px] text-[13px] font-semibold leading-relaxed text-[#7a8699]">{t.old_sub}</p>
            </div>
            <div className="m-4 rounded-[12px] bg-[#f1f1f1] px-6 py-6" style={{ fontFamily: "Arial, Helvetica, sans-serif" }}>
              <img src={JETSPORT_LOGO} alt="Jet Sport" className="mx-auto block h-11 w-auto" />
              <div className="mt-4 text-[15px] font-bold text-[#222]">Kundelogin</div>
              <div className="relative mt-2">
                <div className="flex h-11 items-center border-2 border-[#d5121b] bg-white px-3 text-[15px] text-[#222]">Anders Andersen</div>
                <span className="absolute -right-2.5 top-1/2 -translate-y-1/2 text-[#f4741a]">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="m12 2 2.9 6.1 6.7.8-5 4.6 1.3 6.6L12 17l-5.9 3.1 1.3-6.6-5-4.6 6.7-.8Z" />
                  </svg>
                </span>
              </div>
              <div className="mt-1 text-right text-[11px] font-bold text-[#f4741a]">{t.s1_helperStar}</div>
              <div className="mt-1 flex h-11 items-center border border-[#cfcfcf] bg-white px-3 text-[15px] text-[#9a9a9a]">Skriv din nuværende kode her</div>
              <div className="mt-5 flex h-[46px] items-center justify-center bg-[#d5121b] text-[16px] font-bold text-white">Login</div>
            </div>
            <button
              type="button"
              onClick={() => setShowOldLogin(false)}
              className="w-full border-t border-[#eef0f6] py-3.5 text-[13px] font-bold text-[#8a94a6] transition hover:text-[#343b4a]"
            >
              {t.old_close}
            </button>
          </div>
        </div>
      )}

      <style>{`@keyframes fadeUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}
